'use strict';

/** @type {FoxSellAdditionalSettings} */
const DEFAULT_ADDITIONAL_SETTINGS = {
  quantity_rules: {
    strategy: 'any',
    max: 'cap_at_highest',
  },
  add_on_settings: {
    strategy: 'add_on_step',
  },
};

const FOXSELL_EVENTS = {
  bundleUpdated: 'foxsell:bundle-updated'};

/**
 * @returns {FoxSellBundle}
 */
const emptyBundleState = {
  items: [],
  addOns: {
    addOnStrategy: '',
    enabled: false,
    minimum: 0,
    maximum: 0,
    selectedQuantity: 0,
    isMaximumQuantity: false,
    allowedIds: [],
    items: [],
  },
  isValid: false,
  isAddOnsValid: false,
  isItemsValid: false,
  originalTotalPrice: 0,
  totalPrice: 0,
  totalDiscount: 0,
  id: '',
  qaoEnabled: false,
  priceStrategy: null,
};

/**
 * @returns {FoxSellAddOnsConfig}
 */
const emptyAddOnsConfig = {
  addOnStrategy: 'add_on_step',
  allowedIds: [],
  enabled: false,
  minimum: 0,
  maximum: 0,
  selectedQuantity: 0,
  isMaximumQuantity: false,
};


const DELETE_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
`;

// @ts-check

/**
 * @description Merge a partial additionalSettings object with defaults, ensuring all nested keys exist.
 * @param {FoxSellAdditionalSettings | null | undefined} settings
 * @returns {FoxSellAdditionalSettings}
 */
function resolveAdditionalSettings(settings) {
  return {
    ...settings,
    quantity_rules: {
      ...DEFAULT_ADDITIONAL_SETTINGS.quantity_rules,
      ...settings?.quantity_rules,
    },
    add_on_settings: {
      ...DEFAULT_ADDITIONAL_SETTINGS.add_on_settings,
      ...settings?.add_on_settings,
    },
  };
}

/** @type {number} */
let bodyScrollLockDepth = 0;
/** @type {number} */
let bodyScrollLockY = 0;

/**
 * @description Freeze page scroll (fixed body). Nested callers pair with {@link unlockBodyScroll}.
 * @returns {void}
 */
function lockBodyScroll() {
  if (bodyScrollLockDepth === 0) {
    bodyScrollLockY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${bodyScrollLockY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
  }
  bodyScrollLockDepth += 1;
}

/**
 * @description Undo one {@link lockBodyScroll}; restores scroll when depth returns to 0.
 * @returns {void}
 */
function unlockBodyScroll() {
  if (bodyScrollLockDepth === 0) return;
  bodyScrollLockDepth -= 1;
  if (bodyScrollLockDepth > 0) return;
  const body = document.body;
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.overflow = '';
  window.scrollTo(0, bodyScrollLockY);
}

/**
 * @description Get Item ID from GID
 * @param {string} gid
 * @returns {number}
 */
function getItemIdFromGid(gid) {
  return parseInt(gid.split('/').pop() ?? '0');
}

/**
 * @description List price and compare-at for a variant (Shopify cents).
 * @param {ShopifyVariant[]} variants
 * @param {FoxSellQuantityOption | null | undefined} option
 * @returns {{ price: number; compareAtPrice: number }}
 */
function getVariantPrice(variants, option) {
  if (!option?.variant_id || !option?.price) {
    return { price: 0, compareAtPrice: 0 };
  }
  const variant = variants.find((v) => v.id === parseInt(option.variant_id));
  if (!variant) {
    return { price: 0, compareAtPrice: 0 };
  }
  const price = (option.price.value ?? 0) * 100;
  const compareAtPrice = variant.compare_at_price ?? 0;
  return { price, compareAtPrice };
}

// @ts-check


class FoxSellMixMatch extends HTMLElement {
  constructor() {
    super();
    this.selectedItems = new Map();
    this.selectedAddOns = new Map();
    this.selectedOption = {};
    /** @type {FoxSellMixMatchConfig} */
    this.config = this.getConfig();
    /** @type {FoxSellBundle} */
    this.bundle = emptyBundleState;

    this.boundHandleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    this.validateBundle();
    // Themes may use this class with foxsell--hidden from legacy step flow; ensure ATC is visible
    this.querySelector('.foxsell-add-to-cart-button')?.classList.remove('foxsell--hidden');
    this.querySelector('.foxsell-mix-match__overlay')?.addEventListener('click', this.boundHandleOverlayClick);
  }

  disconnectedCallback() {
    this.querySelector('.foxsell-mix-match__overlay')?.removeEventListener('click', this.boundHandleOverlayClick);
  }

  /**
   * @description Get the config
   * @returns {FoxSellMixMatchConfig}
   */
  getConfig() {
    const config = window.foxsell.config[this.dataset.bundleId ?? ''];
    if(!config) throw new Error('FoxSell Mix Match config not found');
    config.additionalSettings = resolveAdditionalSettings(config.additionalSettings);
    return config;
  }

  handleOverlayClick() {
    /** @type {HTMLElement | null} */
    const modal = this.querySelector('#foxsell-product-dialog[data-modal]');
    if (modal?.hasAttribute('open')) {
      /** @type {any} */ (this.querySelector('foxsell-product-modal'))?.closeModal();
      return;
    }

    const summaryToggle = /** @type {HTMLElement | null} */ (this.querySelector('#foxsell-bundle-summary-toggle'));
    if (summaryToggle?.getAttribute('aria-expanded') === 'true') {
      summaryToggle.click();
    }
  }

  /**
   * @description Get the current price strategy without mutating config
   * @returns {FoxSellPriceConfig | null}
   */
  getCurrentPriceStrategy() {
    const settingsPrice = this.config.settings.price;
    const qaoEnabled = (this.config.options?.length ?? 0) > 0;
    if (!qaoEnabled) return settingsPrice;

    let value = 0;
    const currentValidOption = this.getCurrentValidOption();
    
    if(currentValidOption) {
      value = currentValidOption.price.value;
    }

    return {
      strategy: settingsPrice.strategy,
      value: value,
    };
  }

  /**
   * @description Get the current valid option based on item count
   * @returns {FoxSellQuantityOption | null}
   */
  getCurrentValidOption() {
    const items = this.getSelectedItems();
    const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);
    return this.config.options.findLast(opt => Number(opt.quantity ?? opt) <= itemsCount) ?? null;
  }

  /**
   * @description Build the bundle state from current selection
   * @param {boolean} isValid
   * @param {boolean} isAddOnsValid
   * @param {boolean} isItemsValid
   * @returns {FoxSellBundle}
   */
  buildBundle(isValid, isAddOnsValid, isItemsValid) {
    const qaoEnabled = (this.config.options?.length ?? 0) > 0;
    const items = this.getSelectedItems();
    const addOnItems = this.getSelectedAddOns();
    const { originalTotalPrice, totalPrice, totalDiscount } = this.getTotalPrice();
    const id = this.config.bundleId ? `${this.config.bundleId}_${Date.now()}` : '';
    const priceStrategy = this.getCurrentPriceStrategy();

    return {
      items,
      addOns: {
        ...this.getAddOnsConfig(),
        enabled: qaoEnabled ? this.getAddOnsConfig().enabled : isItemsValid,
        items: addOnItems,
      },
      isValid,
      isAddOnsValid,
      isItemsValid,
      originalTotalPrice,
      totalPrice,
      totalDiscount,
      id,
      qaoEnabled,
      priceStrategy,
    };
  }

  /**
   * @param {string} categoryId
   * @returns {FoxSellCategoryConfig | undefined}
   */
  getCategoryConfig(categoryId) {
    return this.config.categories?.find((category) => category.id === categoryId);
  }

  /**
   * @param {string} categoryId
   * @param {boolean} createIfNotExists
   * @returns {FoxSellCategory | undefined}
   */
  getCategory(categoryId, createIfNotExists = false) {
    const categoryConfig = this.getCategoryConfig(categoryId);
    if (!categoryConfig) return undefined;

    const category = this.selectedItems.get(categoryId);
    if (!category && createIfNotExists) {
      this.selectedItems.set(categoryId, {
        items: new Map(),
        quantity: 0,
        id: categoryId,
        title: categoryConfig.title,
        isMaxQuantity: false,
        maxQuantity: categoryConfig.quantity,
      });
      return this.selectedItems.get(categoryId);
    }
    return category;
  }

  /**
   * @description Get an item from a category
   * @param {number} itemId
   * @param {string} categoryId
   * @returns {FoxSellSelectedItem | undefined}
   */
  getItem(itemId, categoryId) {
    const selectedCategory = this.getCategory(categoryId);
    if (!selectedCategory) return undefined;

    return selectedCategory.items.get(itemId);
  }

  /**
   * @description Get the selected items
   * @returns {FoxSellLineItem[]}
   */
  getSelectedItems() {
    return Array.from(this.selectedItems.values()).flatMap(category => {
      const { items, ...categoryData } = category;
      return Array.from(items.values()).map(item => ({
        ...item,
        category: categoryData,
      }));
    });
  }

  /**
   * @description Get the selected add-ons
   * @returns {FoxSellSelectedItem[]}
   */
  getSelectedAddOns() {
    return Array.from(this.selectedAddOns.values());
  }

  /**
   * @description Add an item to a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToBundle(item, quantity, categoryId, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId, true);
    if (!selectedCategory) return;

    const qaoEnabled = this.config.options.length > 0;
    if (((selectedCategory.quantity + quantity) > selectedCategory.maxQuantity) && !qaoEnabled) return;

    // Inventory check: prevent adding more than available stock
    if (item.inventory_management && item.inventory_policy !== 'continue') {
      const currentQuantity = this.getItem(item.id, categoryId)?.quantity ?? 0;
      if ((currentQuantity + quantity) > item.inventory_quantity) return;
    }

    const selectedItem = this.getItem(item.id, categoryId);
    if (!selectedItem) {
      selectedCategory.items.set(item.id, {
        ...item,
        foxsell_price: this.getItemPrice(item.id, categoryId, item.price),
        quantity: quantity,
      });
    } else {
      selectedItem.quantity += quantity;
    }
    selectedCategory.quantity += quantity;

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add',
        item: this.getItem(item.id, categoryId),
        category: this.selectedItems.get(categoryId),
      }
    }));
  }

  /**
   * @description Remove an item from a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromBundle(item, quantity, categoryId, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId);
    if (!selectedCategory) return;

    const selectedItem = this.getItem(item.id, categoryId);
    if (!selectedItem) return;

    const newItemQuantity = Math.max(selectedItem.quantity - quantity, 0);
    if (newItemQuantity === 0) {
      selectedCategory.items.delete(item.id);
    } else {
      selectedItem.quantity = newItemQuantity;
    }

    selectedCategory.quantity = Math.max(selectedCategory.quantity - quantity, 0);
    const categoryForEvent = selectedCategory.quantity <= 0
      ? { ...selectedCategory, isMaxQuantity: false }
      : null;

    if (selectedCategory.quantity <= 0) {
      this.selectedItems.delete(categoryId);
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    const category = categoryForEvent ?? this.selectedItems.get(categoryId);
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove',
        item: { ...selectedItem, quantity: newItemQuantity },
        category,
      }
    }));
  }

  /**
   * @description Clear the bundle
   * @param {boolean} [dispatchEvent=true]
   * @returns {void}
   */
  clearBundle(dispatchEvent = true) {
    this.selectedItems.clear();
    this.validateBundle();
    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'clear'
      }
    }));
  }

  /**
   * @description Add an add-on to the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToAddOns(item, quantity, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const existingAddOn = this.selectedAddOns.get(item.id);
    if (existingAddOn) {
      existingAddOn.quantity += quantity;
    } else {
      this.selectedAddOns.set(item.id, {
        ...item,
        foxsell_price: this.getItemPrice(item.id, '__add_ons__', item.price),
        quantity: quantity,
      });
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add-addon',
        item: this.selectedAddOns.get(item.id),
      }
    }));
  }

  /**
   * @description Remove an add-on from the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromAddOns(item, quantity, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const existingAddOn = this.selectedAddOns.get(item.id);
    if (!existingAddOn) return;

    const newQuantity = Math.max(existingAddOn.quantity - quantity, 0);
    if (newQuantity === 0) {
      this.selectedAddOns.delete(item.id);
    } else {
      existingAddOn.quantity = newQuantity;
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove-addon',
        item: {
          ...existingAddOn,
          quantity: newQuantity,
        },
      }
    }));
  }

  /**
   * @description Clear all add-ons
   * @param {boolean} [dispatchEvent=true]
   * @returns {void}
   */
  clearAddOns(dispatchEvent = true) {
    this.selectedAddOns.clear();
    if (!dispatchEvent) return;
    this.validateBundle();
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'clear-addons',
      }
    }));
  }

  /**
   * @description Validate the bundle
   * @returns {void}
   */
  validateBundle() {
    const qaoEnabled = this.config.options.length > 0;
    const isItemsValid = qaoEnabled ? this.validateBundleWithQAO() : this.validateBundleWithoutQAO();
    const isAddOnsValid = this.validateAddOns(isItemsValid);

    const isValid = isItemsValid && isAddOnsValid;
    this.bundle = this.buildBundle(isValid, isAddOnsValid, isItemsValid);

    this.updateLineItemProperties();
    this.renderPrice();
    this.toggleAddToCartButton(!isValid);
  }

  /**
   * @description Validate the selected add-ons
   * @param {boolean} isItemsValid
   * @returns {boolean}
   */
  validateAddOns(isItemsValid) {
    const { allowedIds, maximum, addOnStrategy } = this.getAddOnsConfig();

    if(addOnStrategy === 'automatic_add') {
      if(isItemsValid) {
        this.autoAddAddOns();
      }
      return true;
    }

    let selectedQuantity = 0;

    for (const selectedAddOn of this.getSelectedAddOns()) {
      const overMax = (selectedQuantity + selectedAddOn.quantity) > maximum;
      const notAllowed = !allowedIds.includes(selectedAddOn.product.id);
    
      if (notAllowed || overMax) {
        this.selectedAddOns.delete(selectedAddOn.id);
      } else {
        selectedAddOn.foxsell_price = this.getItemPrice(selectedAddOn.id, '__add_ons__', selectedAddOn.price);
        selectedQuantity += selectedAddOn.quantity;
      }
    }

    const selectedAddOns = this.getSelectedAddOns();
    let { minimum: minimumAddOns, maximum: maximumAddOns } = this.getAddOnsConfig();
    const selectedAddOnsQuantity = selectedAddOns.reduce((sum, item) => sum + item.quantity, 0);
    let isValid = selectedAddOnsQuantity >= minimumAddOns && selectedAddOnsQuantity <= maximumAddOns;

    return isValid;
  }

  /**
   * @description Auto add add-ons to bundle
   */
  autoAddAddOns() {
    const { allowedIds, maximum } = this.getAddOnsConfig();

    this.clearAddOns(false);

    let selectedQuantity = 0;
    for (const allowedId of allowedIds) {
      const item = this.config.addOnProducts.find(item => item.id === allowedId);
      if(!item) continue;

      const productGid = `gid://shopify/Product/${allowedId}`;
      const configuredVariantGids = Object.keys(this.config.addOnProductProperties[productGid]?.variants ?? {});
      const variant = item.variants.find(v => configuredVariantGids.includes(`gid://shopify/ProductVariant/${v.id}`)) ?? item.variants[0];

      if(variant) {
        this.selectedAddOns.set(variant.id, {
          ...variant,
          foxsell_price: this.getItemPrice(variant.id, '__add_ons__', variant.price),
          product: {
            ...item,
            featured_image: {
              src: item.featured_image,
              alt: item.title
            },
          },
          quantity: 1,
        });
        selectedQuantity = selectedQuantity + 1;
        if(selectedQuantity >= maximum) break;
      }
    }
  }

  /**
   * @description Get the FoxSell-configured price for a variant within a category (or the add-ons pseudo-category), in cents
   * @param {number} variantId
   * @param {string} categoryId
   * @param {number} fallbackPrice
   * @returns {number}
   */
  getItemPrice(variantId, categoryId, fallbackPrice) {
    if (categoryId === '__add_ons__') {
      const product = this.config.addOnProducts.find(p => p.variants.some(v => v.id === variantId));
      if (!product) return fallbackPrice;

      const qaoEnabled = this.config.options.length > 0;
      if (qaoEnabled) {
        const currentValidOption = this.getCurrentValidOption();
        if (!currentValidOption) return fallbackPrice;

        const addOnEntry = currentValidOption.add_on_products.find(
          (addOn) => parseInt(addOn.id) === product.id
        );
        const value = addOnEntry?.variants[variantId] ?? addOnEntry?.variants[`gid://shopify/ProductVariant/${variantId}`];
        return value != null ? value * 100 : fallbackPrice;
      }

      const pid = `gid://shopify/Product/${product.id}`;
      const vid = `gid://shopify/ProductVariant/${variantId}`;
      const configuredValue = this.config.addOnProductProperties[pid]?.variants[vid];
      return configuredValue != null ? configuredValue * 100 : fallbackPrice;
    }

    const categoryConfig = this.getCategoryConfig(categoryId);
    if (!categoryConfig) return fallbackPrice;

    for (const item of categoryConfig.items) {
      const value = item.variants[variantId] ?? item.variants[`gid://shopify/ProductVariant/${variantId}`];
      if (value != null) return value * 100;
    }

    return fallbackPrice;
  }

  /**
   * @description Validate the bundle with QAO
   * @returns {boolean}
   */
  validateBundleWithQAO() {
    const quantityRules = this.config.additionalSettings.quantity_rules;
    const allowIntermediateQuantity = quantityRules.strategy === 'any';
    const allowOverflow = quantityRules.max === 'no_cap';

    const optionLimits = (this.config.options || [])
      .map(opt => Number(opt.quantity ?? opt))
      .filter(n => !Number.isNaN(n));

    const maxQuantity = optionLimits.length ? Math.max(...optionLimits) : 0;
    const minQuantity = optionLimits.length ? Math.min(...optionLimits) : 0;

    const items = this.getSelectedItems();

    const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

    let isValid = false;

    if (allowIntermediateQuantity) {
      isValid = itemsCount >= minQuantity;
    } else {
      isValid = Array.isArray(this.config.options)
        && this.config.options.some(opt => Number(opt.quantity ?? opt) === itemsCount);
    }

    this.selectedItems.forEach(category => {
      category.isMaxQuantity = !allowOverflow && (itemsCount >= maxQuantity);
    });

    return isValid;
  }

  /**
   * @description Validate the bundle without QAO
   * @returns {boolean}
   */
  validateBundleWithoutQAO() {
    const isValid = this.config.categories.reduce((allValid, category) => {
      const isOptional = category.quantity === 0;
      // If the category is optional, we don't need to validate it
      if (isOptional) return allValid;
      const selectedCategory = this.getCategory(category.id);
      if (!selectedCategory) return false;
      const atMax = selectedCategory.quantity >= selectedCategory.maxQuantity;
      selectedCategory.isMaxQuantity = atMax;
      return allValid && atMax;
    }, true);

    return isValid;
  }

  /**
   * @description Get allowed add-on variant IDs from config with min/max values
   * @returns {FoxSellAddOnsConfig}
   */
  getAddOnsConfig() {
    //! support add-on strategy: add_on_step, automatic_add
    const addOnStrategy = this.config.additionalSettings.add_on_settings.strategy;

    /** @type {number[]} */
    let allowedIds = [];

    let minimum = 0;
    let maximum = 0;

    if(this.config.options.length > 0) {
      const currentValidOption = this.getCurrentValidOption();
      if(!currentValidOption) return { ...emptyAddOnsConfig };
      minimum = currentValidOption.add_on.minimum ?? 0;
      maximum = currentValidOption.add_on.maximum ?? 0;
      //! add_on_products might not exist on older bundle configs, so we need to check if it exists
      //! fallback to addOnProductProperties if it doesn't exist
      if(currentValidOption.add_on_products) {
        allowedIds = currentValidOption.add_on_products.map((addOn)=> parseInt(addOn.id));
      } else {
        allowedIds = Object.keys(this.config.addOnProductProperties).map(key => getItemIdFromGid(key));
      }
    } else {
      minimum = this.config.settings.addOn.minimum ?? 0;
      maximum = this.config.settings.addOn.maximum ?? 0;
      allowedIds = Object.keys(this.config.addOnProductProperties).map(key => getItemIdFromGid(key));
    }

    const isMaximumQuantity = this.getSelectedAddOns().reduce((sum, item) => sum + item.quantity, 0) >= maximum;
    const selectedQuantity = this.getSelectedAddOns().reduce((sum, item) => sum + item.quantity, 0);

    //! If the maximum is 0, we don't need to validate the add-ons
    if(maximum === 0) {
      allowedIds = [];
    }

    return {
      addOnStrategy: addOnStrategy,
      allowedIds: allowedIds,
      enabled: (allowedIds.length > 0 && maximum > 0),
      minimum,
      maximum,
      selectedQuantity,
      isMaximumQuantity
    };
  }

  /**
   * @description Update the line item properties
   * @returns {void}
   */
  updateLineItemProperties() {
    const bundleIdInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_id]"]');
    const itemInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_items]"]');
    const savingsInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_savings]"]');
    const idInput = this.querySelector('input[name="id"]');

    if (!itemInput || !bundleIdInput) return;

    bundleIdInput.setAttribute('value', this.bundle.id);

    if (this.bundle.qaoEnabled) {
      const validOption = this.getCurrentValidOption();
      if (idInput) {
        idInput.setAttribute('value', validOption?.variant_id ?? '');
      }
    }

    const lineItems = this.bundle.items.map(item => ({
      variantId: Number(item.id) || item.id,
      quantity: item.quantity || 1,
      category: item.category.title,
      type: 'product',
      properties: item.properties || {}
    }));

    const addOnLineItems = this.bundle.addOns.items.map(item => ({
      variantId: Number(item.id) || item.id,
      quantity: item.quantity || 1,
      category: '',
      type: 'addOns',
      properties: item.properties || {}
    }));

    lineItems.push(...addOnLineItems);

    itemInput.setAttribute('value', JSON.stringify(lineItems));

    if (savingsInput) {
      const { totalDiscount, totalPrice } = this.bundle;
      if (totalDiscount > 0 && totalPrice > 0) {
        savingsInput.setAttribute('value', window.foxsell?.formatMoney?.(totalDiscount));
      } else {
        savingsInput.setAttribute('value', '');
      }
    }
  }

  /**
   * @description Get the total price of the bundle
   * @returns {FoxSellTotalPrice}
   */
  getTotalPrice() {
    const itemsTotalPrice = this.getSelectedItems().reduce((sum, item) => {
      return sum + (item.foxsell_price ?? item.price) * item.quantity;
    }, 0);

    const addOnsTotalPrice = this.getSelectedAddOns().reduce((sum, item) => {
      return sum + (item.foxsell_price ?? item.price) * item.quantity;
    }, 0);

    let discountValue = 0;
    let totalDiscount = 0;
    let originalTotalPrice = 0;
    let totalPrice = 0;

    const priceStrategy = this.config.settings.price.strategy;
    const qaoEnabled = this.config.options.length > 0;

    if(priceStrategy === 'dynamic_pricing') {
      originalTotalPrice = itemsTotalPrice + addOnsTotalPrice;
      if(qaoEnabled) {
        const validOption = this.getCurrentValidOption();
        if(validOption) {
          discountValue = validOption.price.value;
        }
      } else {
        discountValue = this.config.settings.price.value;
      }
      totalDiscount = (discountValue / 100) * itemsTotalPrice;
      totalPrice = (itemsTotalPrice - totalDiscount) + addOnsTotalPrice;
    } else {
      if(qaoEnabled) {
        const validOption = this.getCurrentValidOption() ?? this.config.options[0];
        if(validOption) {
          const { price, compareAtPrice } = getVariantPrice(this.config.variants, validOption);
          totalPrice = price + addOnsTotalPrice;
          originalTotalPrice = compareAtPrice + addOnsTotalPrice;
          totalDiscount = Math.max(originalTotalPrice - totalPrice, 0);
        }
      } else {
        totalPrice = (this.config.settings.price.value * 100) + addOnsTotalPrice;
        originalTotalPrice = (this.config.variants[0]?.compare_at_price ?? 0) + addOnsTotalPrice;
        totalDiscount = Math.max(originalTotalPrice - totalPrice, 0);
      }
    }

    return {
      originalTotalPrice,
      totalPrice,
      totalDiscount,
      discountValue,
      priceStrategy,
      itemsTotalPrice,
      addOnsTotalPrice
    };
  }

  /**
   * @description Render the price of the bundle on add-to-cart button
   * @returns {void}
   */
  renderPrice() {
    const addToCartButton = this.querySelector('button[type="submit"]');
    if (!addToCartButton) return;

    if (!this.initialAddToCartButtonHTML) {
      this.initialAddToCartButtonHTML = addToCartButton.innerHTML;
    }

    const { originalTotalPrice, totalPrice } = this.bundle;
    if (originalTotalPrice > totalPrice) {
      addToCartButton.innerHTML = `${this.initialAddToCartButtonHTML} -
      <span class="foxsell-compare-at-price">${window.foxsell?.formatMoney?.(originalTotalPrice)}</span>
      <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(totalPrice)}</span>`;
    } else {
      addToCartButton.innerHTML = `${this.initialAddToCartButtonHTML} -
      <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(totalPrice)}</span>`;
    }
  }

  /**
   * @description Toggle the add to cart button disabled state
   * @param {boolean} disable
   * @returns {void}
   */
  toggleAddToCartButton(disable) {
    const addToCartButton = this.querySelector('button[type="submit"]');
    if (!addToCartButton) return;
    addToCartButton.toggleAttribute('disabled', disable);
  }
}

// @ts-check

class HidroFoxSellMixMatch extends FoxSellMixMatch {

  /**
   * @description Update the line item properties
   * @returns {void}
   */
  updateLineItemProperties() {
    const lineItems = this.bundle.items.map(item => ({
      variantId: Number(item.id) || item.id,
      quantity: item.quantity || 1,
      category: item.category.title,
      type: 'product',
      properties: item.properties || {}
    }));

    const addOnLineItems = this.bundle.addOns.items.map(item => ({
      variantId: Number(item.id) || item.id,
      quantity: item.quantity || 1,
      category: '',
      type: 'addOns',
      properties: item.properties || {}
    }));

    lineItems.push(...addOnLineItems);

    /** @type {Array<{ name: string, value: string }>} */
    const requiredInputs = [
      {
        name: 'properties[__foxsell:dynamic_add_on_bundle_items]',
        value: JSON.stringify(lineItems)
      },
      {
        name: 'properties[__foxsell:dynamic_add_on_bundle_id]',
        value: this.bundle.id
      },
      {
        name: 'properties[__foxsell:dynamic_add_on_bundle_savings]',
        value: window.foxsell?.formatMoney?.(this.bundle.totalDiscount)
      }
    ];

    const section = this.closest('.shopify-section');
    if(section) {
      const forms = section.querySelectorAll('form[action*="cart/Add" i]');
      forms.forEach((form) => {
        const bundleIdInput = form.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_id]"]');
        const itemInput = form.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_items]"]');
        const savingsInput = form.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_savings]"]');
        const idInput = form.querySelector('input[name="id"]');

        if(bundleIdInput) {
          bundleIdInput.setAttribute('value', this.bundle.id);
          itemInput?.setAttribute('value', JSON.stringify(lineItems));
          savingsInput?.setAttribute('value', window.foxsell?.formatMoney?.(this.bundle.totalDiscount));
        } else {
          for(const input of requiredInputs) {
            const newInput = document.createElement('input');
            newInput.setAttribute('name', input.name);
            newInput.setAttribute('value', input.value);
            newInput.setAttribute('type', 'hidden');
            newInput.setAttribute('data-foxsell-mix-match', 'true');
            form.appendChild(newInput);
          }
        }

        const variantIdInput = idInput ?? form.querySelector('input[name="id"]');
        if (variantIdInput) {
          const variantId = this.bundle.qaoEnabled
            ? (this.getCurrentValidOption()?.variant_id ?? '')
            : (this.config.variants?.[0]?.id ?? '');
          variantIdInput.setAttribute('value', String(variantId));
        }
      });
    }
  }

  /**
   * @description Toggle the add to cart button disabled state
   * @param {boolean} disable
   * @returns {void}
   */
  toggleAddToCartButton(disable) {
    const section = this.closest('.shopify-section');
    if(section) {
      const forms = section.querySelectorAll('form[action*="cart/Add" i]');
      forms.forEach((form) => {
        const addToCartButton = form.querySelector('button[type="submit"]');
        if (addToCartButton) {
          addToCartButton.toggleAttribute('disabled', disable);
        }
      });
    }
  }

  /**
   * @description Render the price of the bundle on add-to-cart button
   * @returns {void}
   */
  renderPrice() {
    const priceContainer = this.querySelector('.foxsell-mix-match__price-container');
    if(!priceContainer) return;

    const { originalTotalPrice, totalPrice, priceStrategy } = this.bundle;
    const discount = priceStrategy?.value || 0;
    const discountLabel = this.config?.locale.discountLabel.replaceAll("_discount", String(discount));
    if (originalTotalPrice > totalPrice) {
      priceContainer.innerHTML = 
      `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(totalPrice)}</span>
      <span class="foxsell-compare-at-price">${window.foxsell?.formatMoney?.(originalTotalPrice)}</span>
      ${ priceStrategy?.strategy === "dynamic_pricing" && discount > 0 ? 
        `<span class="foxsell-discount">${discountLabel}</span>` 
        : ''
      }
      `;
    } else {
      priceContainer.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(totalPrice)}</span>`;
    }
  }
}

// @ts-check

class FoxSellCategoryHeader extends HTMLElement {
  constructor() {
    super();
    this.categoryId = this.getAttribute('data-category-id');
    this.quantityElement = this.querySelector('.foxsell-category__quantity');
    this.foxsell = this.closest('foxsell-mix-match');
    this._boundUpdateQuantity = this.updateQuantity.bind(this);
  }

  connectedCallback() {
    if (!this.foxsell) return;
    this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this._boundUpdateQuantity);
  }

  disconnectedCallback() {
    if (!this.foxsell || !this._boundUpdateQuantity) return;
    this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this._boundUpdateQuantity);
  }

  /**
   * @description Update the category quantity
   * @param {CustomEvent} event
   * @returns {void}
   */
  updateQuantity(event) {
    if (this.categoryId === '__add_ons__') {
      this.updateAddOnQuantity(event);
      return;
    }
    const { category } = event.detail;
    if (!category) return;
    if (category.id !== this.categoryId) return;
    if (!this.quantityElement) return;
    this.quantityElement.textContent = `(${category.quantity}/${category.maxQuantity})`;
  }

  /**
   * @description Update the add-on quantity
   * @param {CustomEvent} event
   * @returns {void}
   */
  updateAddOnQuantity(event) {
    const { bundle } = event.detail;
    if (!bundle) return;
    if (!this.quantityElement) return;
    this.quantityElement.textContent = `(${bundle.addOns.selectedQuantity}/${bundle.addOns.maximum})`;
  }
}

// @ts-check

class HidroFoxSellCategoryProgress extends HTMLElement {
  constructor() {
    super();
    /** @type {any} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.categoryId = this.getAttribute('data-category-id') ?? '';
    this.categoryTitle = this.getAttribute('data-category-title') ?? '';
    this.boundHandleBundleUpdate = this.handleBundleUpdate.bind(this);

    const configEl = this.querySelector('[data-foxsell-category-progress-config]');
    try {
      this.config = JSON.parse(configEl?.textContent || '{}');
    } catch (error) {
      console.error('Failed to parse foxsell category progress config:', error);
      this.config = {};
    }
  }

  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.labelElement = this.querySelector('.foxsell-category-progress__label');
    /** @type {HTMLElement | null} */
    this.trackElement = this.querySelector('.foxsell-category-progress__track');
    /** @type {HTMLElement | null} */
    this.barElement = this.querySelector('.foxsell-category-progress__bar');

    // Must be resolved before the first update — see isAutoAddCategory()
    this.autoAddCategory = this.isAutoAddCategory();

    this.handleBundleUpdate();
    this.foxsell?.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
  }

  disconnectedCallback() {
    this.foxsell?.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
  }

  /**
   * @description A category whose products are auto-added on load is complete the moment the
   * page renders, so a progress bar there is meaningless. Detected from the DOM: `product-card.liquid`
   * only emits `data-auto-add-quantity` when `auto_add_quantity > 0`.
   * @returns {boolean}
   */
  isAutoAddCategory() {
    const categoryElement = this.closest('.foxsell-category__item');
    return Boolean(categoryElement?.querySelector('foxsell-product-card[data-auto-add-quantity]'));
  }

  /**
   * @description Compute this category's progress from the bundle state
   * @returns {FoxSellCategoryProgressData | undefined}
   */
  getCategoryProgress() {
    if (!this.foxsell?.config || !this.foxsell.bundle) return undefined;
    if (this.autoAddCategory) return undefined;
    const bundle = this.foxsell.bundle;

    let currentQuantity = 0;
    let requiredQuantity = 0;

    if (this.categoryId === '__add_ons__') {
      currentQuantity = bundle.addOns?.selectedQuantity ?? 0;
      requiredQuantity = bundle.addOns?.maximum ?? 0;
    } else {
      const categoryConfig = this.foxsell.config.categories?.find(
        (/** @type {FoxSellCategoryConfig} */ category) => category.id === this.categoryId
      );
      requiredQuantity = categoryConfig?.quantity ?? 0;
      currentQuantity = bundle.items
        .filter((/** @type {FoxSellLineItem} */ item) => item.category.id === this.categoryId)
        .reduce((/** @type {number} */ sum, /** @type {FoxSellLineItem} */ item) => sum + item.quantity, 0);
    }

    // Optional category (target 0) — nothing meaningful to show
    if (requiredQuantity <= 0) return undefined;

    const remainingQuantity = Math.max(requiredQuantity - currentQuantity, 0);
    const priceStrategy = bundle.priceStrategy;
    /** @type {string} */
    let discount = '';
    if (priceStrategy?.strategy === 'dynamic_pricing') {
      discount = (priceStrategy.value ?? 0) + '%';
    } else if (priceStrategy) {
      discount = window.foxsell?.formatMoney?.(parseFloat(String(priceStrategy.value ?? 0)) * 100) ?? '';
    }

    return {
      categoryTitle: this.categoryTitle,
      currentQuantity,
      requiredQuantity,
      remainingQuantity,
      discount,
      isCompleted: currentQuantity >= requiredQuantity,
      isEmpty: currentQuantity === 0,
      progress: Math.min(Math.round((currentQuantity / requiredQuantity) * 100), 100),
    };
  }

  /**
   * @description Replace placeholders in the configured message (same texts as Bundle Summary)
   * @param {FoxSellCategoryProgressData} data
   * @returns {string}
   */
  formatMessage(data) {
    let message = '';
    if (data.isEmpty) {
      message = this.config.emptyStateText ?? '';
    } else if (data.isCompleted) {
      message = this.config.endStateText ?? '';
    } else {
      message = this.config.progressStateText ?? '';
    }

    return String(message)
      .replaceAll('_categoryTitle', String(data.categoryTitle))
      .replaceAll('_currentQuantity', String(data.currentQuantity))
      .replaceAll('_requiredQuantity', String(data.requiredQuantity))
      .replaceAll('_remainingQuantity', String(data.remainingQuantity))
      .replaceAll('_discount', String(data.discount ?? ''));
  }

  /**
   * @description Handle the bundle update
   * @returns {void}
   */
  handleBundleUpdate() {
    const data = this.getCategoryProgress();

    // Hide for optional categories / when state is not computable
    if (!data) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    if (this.labelElement) {
      this.labelElement.innerHTML = this.formatMessage(data);
    }

    if (this.trackElement) {
      this.trackElement.setAttribute('aria-valuenow', String(data.progress));
      this.trackElement.setAttribute('aria-valuetext', `${data.currentQuantity} of ${data.requiredQuantity}`);
    }

    if (this.barElement) {
      this.barElement.style.setProperty('--foxsell-category-progress', String(data.progress / 100));
    }

    this.classList.toggle('foxsell-category-progress--complete', data.isCompleted);
  }
}

// @ts-check

class FoxSellProductCard extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');

    /** @type {FoxSellVariantRadio | FoxSellVariantSelect | null} */
    this.variantSelector = this.querySelector('foxsell-variant-radio') || this.querySelector('foxsell-variant-select');
    this.disableAddToBundle = false;
    this.boundAddToBundle = this.addToBundle.bind(this);
    this.boundRemoveFromBundle = this.removeFromBundle.bind(this);
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    this.boundHandleBundleUpdated = this.handleBundleUpdated.bind(this);
    this.categoryId = this.getAttribute('data-category') || null;
    this.productId = parseInt(this.getAttribute('data-product-id') || '0');
    this.isAddOnCard = this.categoryId === '__add_ons__';

    if (this.isAddOnCard) {
      this.disableAddToBundle = true;
      this.toggleAddToBundleButton(true);
    }
  }

  connectedCallback() {
    this.querySelectorAll('.add-to-bundle').forEach(btn => btn.addEventListener('click', this.boundAddToBundle));
    this.querySelectorAll('.remove-from-bundle').forEach(btn => btn.addEventListener('click', this.boundRemoveFromBundle));
    if (this.variantSelector) {
      this.addEventListener('variant-change', this.boundHandleVariantChange);
    }

    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdated);
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('.add-to-bundle').forEach(btn => btn.removeEventListener('click', this.boundAddToBundle));
    this.querySelectorAll('.remove-from-bundle').forEach(btn => btn.removeEventListener('click', this.boundRemoveFromBundle));
    if (this.variantSelector) {
      this.removeEventListener('variant-change', this.boundHandleVariantChange);
    }

    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdated);
    }
  }

  handleVariantChange() {
    this.updateFeaturedImage();
    this.updatePrice();
    this.updateQuantity(this.getCurrentQuantity());

    if (!this.variantSelector?.currentVariant) {
      this.disableAddToBundle = true;
      this.toggleAddToBundleButton(true);
      return;
    }

    let disable;
    if (this.categoryId === '__add_ons__') {
      const bundle = this.foxsell?.bundle;
      if (!bundle) {
        disable = true;
      } else {
        const { enabled, allowedIds, isMaximumQuantity } = bundle.addOns;
        const isAllowed = allowedIds.includes(this.variantSelector.product?.id ?? 0);
        disable = !enabled || !isAllowed || isMaximumQuantity || this.isCurrentVariantAtInventoryLimit();
      }
    } else {
      const category = this.foxsell?.getCategory(this.categoryId ?? '');
      disable = (category?.isMaxQuantity ?? false) || this.isCurrentVariantAtInventoryLimit();
    }

    this.disableAddToBundle = disable;
    this.toggleAddToBundleButton(disable);
  }

  /**
   * @description Handle the bundle updated event
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  handleBundleUpdated(event) {
    if (event.detail.action === 'clear') {
      this.updateQuantity(0);
      const atInventoryLimit = this.isCurrentVariantAtInventoryLimit();
      this.disableAddToBundle = atInventoryLimit;
      this.toggleAddToBundleButton(atInventoryLimit);
      return;
    }

    if (this.categoryId === '__add_ons__') {
      this.handleAddOnUpdated(event);
      return;
    }

    if (!this.foxsell || !this.foxsell.config) return;

    const qaoEnabled = this.foxsell.config.options.length > 0;

    if (event.detail.action === 'refresh') {
      this.updateQuantity(this.getCurrentQuantity());
      if (qaoEnabled) this.updatePrice();
      return;
    }

    if (event.detail.action !== 'add' && event.detail.action !== 'remove') return;

    const { category } = event.detail;
    const disableAddToBundle = category.isMaxQuantity;

    if (!qaoEnabled && this.categoryId !== category.id) return;

    this.disableAddToBundle = disableAddToBundle || this.isCurrentVariantAtInventoryLimit();
    this.toggleAddToBundleButton(this.disableAddToBundle);

    if (qaoEnabled) this.updatePrice();
    this.updateQuantity(this.getCurrentQuantity());
  }

  /**
   * @description Handle the add-on updated event
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  handleAddOnUpdated(event) {
    const { action } = event.detail;

    if (action === 'clear-addons') {
      this.updateQuantity(0);
      return;
    }

    if (action === 'refresh') {
      this.updateQuantity(this.getCurrentQuantity());
      return;
    }

    const { item, bundle } = event.detail;
    if (!item || !bundle) return;

    const { enabled, allowedIds, isMaximumQuantity } = bundle.addOns;

    const isAllowed = allowedIds.includes(this.variantSelector?.product?.id ?? 0);

    if (isAllowed) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }

    const disableAddToBundle = !enabled || !isAllowed || isMaximumQuantity;

    this.disableAddToBundle = disableAddToBundle || this.isCurrentVariantAtInventoryLimit();
    this.toggleAddToBundleButton(this.disableAddToBundle);
    this.updatePrice();
    this.updateQuantity(this.getCurrentQuantity());
  }

  /**
   * @description Get the current quantity
   * @returns {number}
   */
  getCurrentQuantity() {
    const variantId = this.variantSelector?.currentVariant?.id;
    if (variantId === undefined || variantId === null || !this.foxsell?.bundle || !this.categoryId) return 0;

    /* check if the variant is an add-on */
    if (this.categoryId === '__add_ons__') {
      const addOns = this.foxsell.bundle.addOns;
      if (!addOns || !addOns.items) return 0;
      return addOns.items.find((item) => item.id === variantId)?.quantity ?? 0;
    }

    /* else if it is a regular product */
    return this.foxsell.bundle.items
      .find(item => item.category.id === this.categoryId && item.id === variantId)
      ?.quantity ?? 0;
  }

  /**
   * @description Update the featured image
   * @returns {void}
   */
  updateFeaturedImage() {
    if (!this.variantSelector || !this.variantSelector.currentVariant) return;
    const img = this.querySelector('.foxsell-product-card__image');
    const image = this.variantSelector.currentVariant?.featured_image || this.variantSelector.currentVariant?.product?.featured_image;
    if (!img || !image?.src) return;
    img.setAttribute('src', image.src);
    if (image.width != null) img.setAttribute('srcset', image.src + (image.src.includes('?') ? '&' : '?') + 'width=' + image.width);
  }

  /**
   * @description Update the price
   * @returns {void}
   */
  updatePrice() {
    if (!this.variantSelector || !this.foxsell || !this.foxsell.bundle || !this.categoryId) return;
    const priceEl = this.querySelector('.foxsell-product-card__price');
    if (!priceEl) return;

    const { currentVariant, product } = this.variantSelector;
    if (!currentVariant) return;

    const fallbackPrice = currentVariant.price ?? currentVariant.product?.price ?? product?.price ?? 0;
    let price = this.foxsell.getItemPrice(currentVariant.id, this.categoryId, fallbackPrice);
    let discountedPrice = price;
    const priceStrategy = this.foxsell.bundle.priceStrategy;
    if (!this.isAddOnCard && priceStrategy && priceStrategy.strategy === 'dynamic_pricing') {
      const discount = priceStrategy.value;
      discountedPrice = price - (price * (discount / 100));
    }

    if (price > discountedPrice) {
      priceEl.innerHTML = `
      <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(discountedPrice)}</span>
      <span class="foxsell-compare-at-price">${window.foxsell?.formatMoney?.(price)}</span>`;
    } else {
      priceEl.innerHTML = `<span class="foxsell-sale-price">${this.isAddOnCard && price === 0 ? 'Free' : window.foxsell?.formatMoney?.(price)}</span>`;
    }
  }

  /**
   * @description Update the quantity
   * @param {number} quantity
   * @returns {void}
   */
  updateQuantity(quantity) {
    const el = this.querySelector('.foxsell-quantity');
    if (el) el.textContent = String(quantity);
  }

  /**
   * @description Whether the selected variant is at stock cap (bundle line or add-on; see getCurrentQuantity)
   * @returns {boolean}
   */
  isCurrentVariantAtInventoryLimit() {
    const variant = this.variantSelector?.currentVariant;
    if (!variant || !this.foxsell || !this.categoryId) return true;
    if (!variant.inventory_management || variant.inventory_policy === 'continue') return false;
    return this.getCurrentQuantity() >= variant.inventory_quantity;
  }

  /**
   * @description Add item to bundle
   * @returns {void}
   */
  addToBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.addToAddOns(this.variantSelector.currentVariant, 1);
      if (this.isCurrentVariantAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    } else {
      this.foxsell.addToBundle(this.variantSelector.currentVariant, 1, this.categoryId);
      if (this.isCurrentVariantAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    }
  }

  /**
   * @description Remove item from bundle
   * @returns {void}
   */
  removeFromBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.removeFromAddOns(this.variantSelector.currentVariant, 1);
    } else {
      this.foxsell.removeFromBundle(this.variantSelector.currentVariant, 1, this.categoryId);
    }
  }

  /**
   * @description Toggle the add to bundle button
   * @param {boolean} disable
   * @returns {void}
   */
  toggleAddToBundleButton(disable) {
    this.querySelectorAll('.add-to-bundle').forEach(btn => {
      btn.toggleAttribute('disabled', disable || this.disableAddToBundle);
    });
  }
}

// FoxSell Product Options
class FoxSellVariantRadio extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellProductCard | null} */
    this.productCard = null;
    this.boundOnVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.productCard = this.closest('foxsell-product-card');
    this.onVariantChange();
    this.addEventListener('change', this.boundOnVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.boundOnVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateOptionLabels();
    this.updateVariantStatuses();
    // Dispatch variant change event to the product card
    this.dispatchEvent(new CustomEvent('variant-change', { bubbles: true }));
  }

  updateOptions() {
    this.options = [...this.querySelectorAll('fieldset')].map(fs =>
      fs.querySelector('input:checked')?.getAttribute('value') ?? ''
    );
  }

  /**
   * @description Get the FoxSell price for a variant, handling both numeric ID and GID format
   * @param {any} allowedVariants
   * @param {number} variantId
   * @returns {number}
   */
  getFoxSellPrice(allowedVariants, variantId) {
    if (!allowedVariants || !variantId) return 0;
    let foxsell_price = 0;

    if (allowedVariants[variantId]) {
      foxsell_price = allowedVariants[variantId] * 100;
    } else if (allowedVariants[`gid://shopify/ProductVariant/${variantId}`]) {
      foxsell_price = allowedVariants[`gid://shopify/ProductVariant/${variantId}`] * 100;
    }

    return foxsell_price;
  }

  /**
   * @description Get the variant data
   * @returns {FoxSellVariant[]}
   */
  getVariantData() {
    if (this.variantData) return this.variantData;
    try {
      const parsed = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '{}');

      /** @type {any[]} */
      const raw = Array.isArray(parsed.available_variants) ? parsed.available_variants : (Array.isArray(parsed) ? parsed : []);

      /** @type {FoxSellVariant[]} */
      this.variantData = raw.map(v => ({
        ...v,
        product: parsed.product ?? null,
        foxsell_price: this.getFoxSellPrice(parsed.allowed_variants, v.id)
      }));

      /** @type {FoxSellProduct | null} */
      this.product = parsed.product ?? null;
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      /** @type {FoxSellVariant[]} */
      this.variantData = [];
    }
    return this.variantData ?? [];
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(v =>
      v.options?.every((opt, i) => opt === this.options?.[i])
    );
  }

  updateOptionLabels() {
    this.querySelectorAll('fieldset').forEach((fieldset, i) => {
      const span = fieldset.querySelector('.foxsell-variant-radio__option-value, .foxsell-variant-select__option-value');
      if (span) span.textContent = this.options?.[i] ?? '';
    });
  }

  updateVariantStatuses() {
    const firstChecked = this.querySelector(':checked');
    if (!firstChecked) return;

    if (!this.variantData) return;
    const selectedOptionOneVariants = this.variantData.filter(variant => firstChecked.getAttribute('value') === variant.option1);
    const inputWrappers = [...this.querySelectorAll('fieldset')];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousFieldset = inputWrappers[index - 1];
      const previousChecked = previousFieldset?.querySelector(':checked');

      if (!previousChecked) return;

      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(variant => variant.available && variant[`option${index}`] === previousChecked.getAttribute('value'))
        .map(variantOption => variantOption[`option${index + 1}`] ?? '')
        .filter(v => v !== '');

      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  /**
   * @description Set the input availability
   * @param {Element[]} listOfOptions
   * @param {string[]} listOfAvailableOptions
   * @returns {void}
   */
  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      input.toggleAttribute('disabled', !listOfAvailableOptions.includes(input.getAttribute('value') ?? ''));
    });
  }
}

class FoxSellVariantSelect extends FoxSellVariantRadio {
  updateOptions() {
    this.options = [...this.querySelectorAll('fieldset')].map(fs => {
      const select = fs.querySelector('select');
      if (select) return select.value;
      return fs.querySelector('input:checked')?.getAttribute('value') ?? '';
    });
  }
}

// @ts-check

class HidroFoxSellProductCard extends FoxSellProductCard {
  constructor() {
    super();
    this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
    this.boundHandleQuantityKeydown = this.handleQuantityKeydown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.isAutoAddCard()) {
      this.classList.add('foxsell-product-card--auto-add');
    }
    this.bindQuantityInput();
    // Wait for nested variant selector to initialize currentVariant
    queueMicrotask(() => this.autoAddDefaultQuantity());
  }

  disconnectedCallback() {
    this.unbindQuantityInput();
    super.disconnectedCallback();
  }

  /**
   * @description Bind editable quantity input listeners
   * @returns {void}
   */
  bindQuantityInput() {
    if (this.isAutoAddCard()) return;
    const input = this.querySelector('.foxsell-quantity');
    if (!(input instanceof HTMLInputElement)) return;
    input.addEventListener('change', this.boundHandleQuantityChange);
    input.addEventListener('keydown', this.boundHandleQuantityKeydown);
  }

  /**
   * @description Unbind editable quantity input listeners
   * @returns {void}
   */
  unbindQuantityInput() {
    const input = this.querySelector('.foxsell-quantity');
    if (!(input instanceof HTMLInputElement)) return;
    input.removeEventListener('change', this.boundHandleQuantityChange);
    input.removeEventListener('keydown', this.boundHandleQuantityKeydown);
  }

  /**
   * @description Whether this card auto-adds its quantity on load
   * @returns {boolean}
   */
  isAutoAddCard() {
    return parseInt(this.getAttribute('data-auto-add-quantity') || '0', 10) > 0;
  }

  /**
   * @description When a product has only one available variant, pre-add the category quantity
   * @param {boolean} [retried]
   * @returns {void}
   */
  autoAddDefaultQuantity(retried = false) {
    if (this.isAddOnCard || !this.foxsell || !this.categoryId) return;

    const autoAddQuantity = parseInt(this.getAttribute('data-auto-add-quantity') || '0', 10);
    if (autoAddQuantity < 1) return;

    const variant = this.variantSelector?.currentVariant;
    if (!variant) {
      if (!retried) requestAnimationFrame(() => this.autoAddDefaultQuantity(true));
      return;
    }

    if (this.getCurrentQuantity() > 0) return;

    this.foxsell.addToBundle(variant, autoAddQuantity, this.categoryId);
  }

  /**
   * @description Prevent changing quantity on auto-added items
   * @returns {void}
   */
  addToBundle() {
    if (this.isAutoAddCard()) return;
    super.addToBundle();
  }

  /**
   * @description Prevent removing auto-added items
   * @returns {void}
   */
  removeFromBundle() {
    if (this.isAutoAddCard()) return;
    super.removeFromBundle();
  }

  /**
   * @description Apply typed quantity to the bundle on change/blur
   * @param {Event} event
   * @returns {void}
   */
  handleQuantityChange(event) {
    if (this.isAutoAddCard()) return;
    if (!this.variantSelector?.currentVariant || !this.foxsell || !this.categoryId) {
      this.updateQuantity(0);
      return;
    }

    const input = /** @type {HTMLInputElement} */ (event.target);
    let target = parseInt(input.value, 10);
    if (Number.isNaN(target) || target < 0) target = 0;

    const current = this.getCurrentQuantity();
    if (target === current) {
      input.value = String(current);
      return;
    }

    const variant = this.variantSelector.currentVariant;
    const delta = target - current;

    if (delta > 0) {
      let addQty = delta;

      if (this.categoryId === '__add_ons__') {
        const addOns = this.foxsell.bundle?.addOns;
        if (addOns?.maximum > 0) {
          const selectedQty = addOns.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) ?? 0;
          addQty = Math.min(addQty, Math.max(addOns.maximum - selectedQty, 0));
        }
        if (variant.inventory_management && variant.inventory_policy !== 'continue') {
          addQty = Math.min(addQty, Math.max(variant.inventory_quantity - current, 0));
        }
        if (addQty > 0) {
          // @ts-ignore
          this.foxsell.addToAddOns(variant, addQty);
        }
      } else {
        const qaoEnabled = (this.foxsell.config?.options?.length ?? 0) > 0;
        if (!qaoEnabled) {
          const category = this.foxsell.getCategory(this.categoryId);
          const maxQuantity = category?.maxQuantity
            ?? this.foxsell.getCategoryConfig?.(this.categoryId)?.quantity
            ?? Infinity;
          const categoryQty = category?.quantity ?? 0;
          addQty = Math.min(addQty, Math.max(maxQuantity - categoryQty, 0));
        }
        if (variant.inventory_management && variant.inventory_policy !== 'continue') {
          addQty = Math.min(addQty, Math.max(variant.inventory_quantity - current, 0));
        }
        if (addQty > 0) {
          this.foxsell.addToBundle(variant, addQty, this.categoryId);
        }
      }
    } else {
      const removeQty = -delta;
      if (this.categoryId === '__add_ons__') {
        // @ts-ignore
        this.foxsell.removeFromAddOns(variant, removeQty);
      } else {
        this.foxsell.removeFromBundle(variant, removeQty, this.categoryId);
      }
    }

    // Sync to actual quantity in case the request was clamped
    this.updateQuantity(this.getCurrentQuantity());
  }

  /**
   * @description Prevent form submit; apply quantity on Enter
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleQuantityKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    /** @type {HTMLInputElement} */ (event.target).blur();
  }

  /**
   * @param {number} quantity
   */
  updateQuantity(quantity) {
    if (this.isAutoAddCard()) {
      const el = this.querySelector('.foxsell-quantity');
      if (el) el.textContent = `x${quantity}`;
      this.classList.toggle('item-in-bundle', quantity > 0);
      return;
    }

    const el = this.querySelector('.foxsell-quantity');
    if (el instanceof HTMLInputElement) {
      if (document.activeElement !== el) {
        el.value = String(quantity);
      }
    } else if (el) {
      el.textContent = String(quantity);
    }
    this.classList.toggle('item-in-bundle', quantity > 0);
  }
}

// @ts-check

class FoxSellBundleSummary extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundUpdateBundleSummary = this.updateBundleSummary.bind(this);
    this.boundTogglePanel = this.handleTogglePanel.bind(this);
  }

  connectedCallback() {
    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundUpdateBundleSummary);
    }

    this.toggleButton = this.querySelector('#foxsell-bundle-summary-toggle');
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.boundTogglePanel);
    }
  }

  disconnectedCallback() {
    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundUpdateBundleSummary);
    }
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.boundTogglePanel);
    }
    if (this.toggleButton?.getAttribute('aria-expanded') === 'true') {
      unlockBodyScroll();
    }
  }

  /**
   * @description Toggle collapsible items panel (mobile disclosure)
   * @returns {void}
   */
  handleTogglePanel() {
    const btn = this.toggleButton;
    if (!btn) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (expanded) {
      unlockBodyScroll();
    } else {
      lockBodyScroll();
    }
  }

  /**
   * @description Update the bundle summary
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  updateBundleSummary(event) {
    const { bundle } = event.detail;

    //Update the header total quantity
    this.updateHeaderTotalQuantity(bundle);
    const bundleItemsContainer = this.querySelector('.foxsell-bundle-summary__items-list');
    if (!bundle || !bundleItemsContainer) return

    if (!this.emptyStateHTML) {
      this.emptyStateHTML = bundleItemsContainer.innerHTML;
    }

    const items = bundle.items;

    const addOnItems = bundle.addOns.items.map(item => ({
      ...item,
      category: {
        id: '__add_ons__',
        title: 'AddOns',
        quantity: bundle.addOns.selectedQuantity,
        maxQuantity: bundle.addOns.maximum,
        isMaxQuantity: bundle.addOns.isMaximumQuantity
      }
    }));

    const allItems = [...items, ...addOnItems];

    if (!allItems || allItems.length === 0) {
      bundleItemsContainer.innerHTML = this.emptyStateHTML;
      return;
    }

    bundleItemsContainer.innerHTML = allItems.map(item => {
      return this.renderLineItem(item);
    }).join('');
  }

  /**
   * @param {FoxSellBundle | null | undefined} bundle
   * @returns {void}
   */
  updateHeaderTotalQuantity(bundle) {
    const el = this.querySelector('[data-foxsell-total-quantity]');
    if (!el) return;
    if (!bundle) {
      el.textContent = '';
      return;
    }
    const { items, addOns } = bundle;
    let total = 0;
    for (let i = 0; i < items.length; i++) total += items[i].quantity;
    for (let i = 0; i < addOns.items.length; i++) total += addOns.items[i].quantity;
    el.textContent = `(${total})`;
  }

  //! DO NOT MODIFY THIS METHOD, extend the class to override this method
  /**
   * @description Render the line item
   * @param {FoxSellLineItem} item
   * @returns {string}
   */
  renderLineItem(item) {
    let itemImage = item.featured_image ? item.featured_image.src : item.product.featured_image?.src;
    if (itemImage) {
      const separator = itemImage.includes('?') ? '&' : '?';
      itemImage = itemImage + separator + 'width=150';
    }
    let itemPrice = item.foxsell_price;
    let discountedPrice = item.foxsell_price;
    let discount = 0;
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return '';
    const priceStrategy = this.foxsell.bundle.priceStrategy;

    //! Only apply price strategy to items, not add-ons
    if (item.category.id !== '__add_ons__') {
      if (priceStrategy && priceStrategy.strategy === 'dynamic_pricing') {
        discount = priceStrategy.value;
        discountedPrice = item.foxsell_price - (item.foxsell_price * (discount / 100));
      }
    }

    const addOnStrategy = this.foxsell.config.additionalSettings.add_on_settings.strategy;

    return (`
      <foxsell-bundle-line-item data-item-id="${item.id}" data-category-id="${item.category.id}" data-category-title="${item.category.title}" data-quantity="${item.quantity}" class="foxsell-bundle-summary__item">
        <div><img src="${itemImage}"/></div>
        <div class="foxsell-bundle-summary__item-info">
          ${item.category.id === '__add_ons__' ? `<span class="foxsell-bundle-summary__item-add-on-tag">${this.foxsell.config.locale.addOnsLineItemLabel}</span>` : ''}
          <div class="foxsell-bundle-summary__item-title">${item.product.title}</div>
          ${item.option1 !== 'Default Title' ? `<div>${item.options.join(", ")}</div>` : ''}
          ${(item.category.id === '__add_ons__' || priceStrategy?.strategy === 'dynamic_pricing') ? `
          <div>
            ${itemPrice > discountedPrice ? `
              <div>
                <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(discountedPrice)}</span>
                <span class="foxsell-compare-at-price">${window.foxsell?.formatMoney?.(itemPrice)}</span>
                ${discount > 0 ? `<span>(${discount}% off)</span>`: ''}
              </div>`
              :
              `<div>
                <span class="foxsell-sale-price">${item.category.id === '__add_ons__' && itemPrice === 0 ? 'Free' : window.foxsell?.formatMoney?.(itemPrice)}</span>
              </div>`
            }
          </div>` : ''}
        </div>
        <div class="foxsell-bundle-summary__quantity">x ${item.quantity}</div>
        ${item.category.id === '__add_ons__' && addOnStrategy === 'automatic_add' ? ""
          : `
          <div>
            <button class="foxsell--button foxsell--button-ghost foxsell-bundle-summary__item-delete" aria-label="Remove item from bundle">
              ${DELETE_ICON_SVG}
            </button>
          </div>`
        }
      </foxsell-bundle-line-item>
    `)
  }
}

class FoxSellBundleLineItem extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundHandleItemDelete = this.handleItemDelete.bind(this);
  }

  connectedCallback() {
    this.deleteButton = this.querySelector('button.foxsell-bundle-summary__item-delete');
    if (this.deleteButton) {
      this.deleteButton.addEventListener('click', this.boundHandleItemDelete);
    }
  }

  disconnectedCallback() {
    if (this.deleteButton) {
      this.deleteButton.removeEventListener('click', this.boundHandleItemDelete);
    }
  }

  handleItemDelete() {
    if (!this.foxsell) return;

    if (this.dataset.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.removeFromAddOns({ id: parseInt(this.dataset.itemId) }, parseInt(this.dataset.quantity || '1'));
    } else {
      // @ts-ignore
      this.foxsell.removeFromBundle({ id: parseInt(this.dataset.itemId) }, parseInt(this.dataset.quantity || '1'), this.dataset.categoryId);
    }
  }
}

class FoxSellBundleProgress extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundHandleBundleUpdate = this.handleBundleUpdate.bind(this);
    const configEl = this.querySelector('#foxsell-bundle-progress-config');

    try {
      this.config = JSON.parse(configEl?.textContent || '{}');
    } catch (error) {
      console.error('Failed to parse foxsell bundle progress config:', error);
      this.config = {};
    }
  }

  connectedCallback() {
    this.handleBundleUpdate();

    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
    }
  }

  disconnectedCallback() {
    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
    }
  }

  /**
   * @description Handle the bundle update
   * @returns {void}
   */
  handleBundleUpdate() {
    if (this.dataset.type === 'summary-header') {
      this.updateSummaryHeader();
    } else if (this.dataset.type === 'progress-bar') {
      this.updateProgressBar();
    }
  }

  /**
   * @description Update the summary header QAO list. Scoped to `data-type="summary-header"` in bundle-summary.liquid.
   * @returns {void}
   */
  updateSummaryHeader() {
    if (!this.foxsell?.config?.options?.length) return;

    const options = this.foxsell.config.options;
    const currentValidOption = this.foxsell.getCurrentValidOption() ?? options[0];
    const selectedId = String(currentValidOption?.variant_id ?? '');

    this.querySelectorAll('.foxsell-bundle-progress__item').forEach((el) => {
      el.classList.toggle('active', String(el.getAttribute('data-id') ?? '') === selectedId);
    });

    const priceValueEl = this.querySelector('.foxsell-bundle-progress__price-value');
    if (!priceValueEl) return;

    const { originalTotalPrice, totalPrice } = this.foxsell.bundle ?? {};
    const formatMoney = window.foxsell.formatMoney;

    if (originalTotalPrice > totalPrice) {
      priceValueEl.innerHTML = `
        <span class="foxsell-compare-at-price">${formatMoney(originalTotalPrice)}</span>
        <span class="foxsell-sale-price">${formatMoney(totalPrice)}</span>
      `;
    } else {
      priceValueEl.innerHTML = `<span class="foxsell-sale-price">${formatMoney(totalPrice)}</span>`;
    }
  }

  /**
   * @param {FoxSellBundleProgressConfig} messageConfig
   * @returns {string}
   */
  formatMessage(messageConfig) {
    const { currentStep, maxSteps, currentQuantity, requiredQuantity, remainingQuantity, discount, isCompleted } = messageConfig;
    /** @type {string} */
    let message = '';

    if (currentStep === 0) {
      message = this.config.emptyStateText;
    } else if (isCompleted) {
      message = this.config.endStateText;
    } else {
      message = this.config.progressStateText;
    }

    message = message
    .replaceAll('_currentStep', String(currentStep))
    .replaceAll('_maxSteps', String(maxSteps))
    .replaceAll('_remainingQuantity', String(remainingQuantity))
    .replaceAll('_currentQuantity', String(currentQuantity))
    .replaceAll('_requiredQuantity', String(requiredQuantity))
    .replaceAll('_discount', String(discount));

    return message
  }

  /**
   * @description Get the bundle progress without QAO
   * @returns {FoxSellBundleProgressConfig | undefined}
   */
  getBundleProgressWithoutQAO() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return undefined;
    const bundle = this.foxsell.bundle.items;
    const maxSteps = this.foxsell.config.categories.reduce((acc, category) => acc + category.quantity, 0);
    const currentStep = bundle.reduce((acc, item) => acc + item.quantity, 0);
    const priceStrategy = this.foxsell.bundle.priceStrategy;
    let discount;
    if (priceStrategy?.strategy === 'dynamic_pricing') {
      discount = (priceStrategy.value ?? 0) + '%';
    } else {
      discount = window.foxsell?.formatMoney?.(parseFloat(String(priceStrategy?.value ?? 0)) * 100);
    }

    return {
      currentStep,
      maxSteps,
      discount,
      currentQuantity: currentStep,
      requiredQuantity: maxSteps,
      remainingQuantity: maxSteps - currentStep,
      isCompleted: currentStep === maxSteps,
      progress: Math.min(Math.round((currentStep / maxSteps) * 100), 100)
    };
  }


  /**
   * @description Get the bundle progress with QAO
   * @returns {FoxSellBundleProgressConfig | undefined}
   */
  getBundleProgressWithQAO() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return;
    const bundle = this.foxsell.bundle.items;
    const maxSteps = this.foxsell.config.options.length;
    const bundleQuantity = bundle.reduce((acc, item) => acc + item.quantity, 0);

    let eligibleOptionIndex = this.foxsell.config.options.findIndex(option => option.quantity > bundleQuantity);

    let optionIndex = eligibleOptionIndex === -1 ? maxSteps - 1 : eligibleOptionIndex;

    const option = this.foxsell.config.options[optionIndex];
    const price = option?.price;

    let discount;
    if (price?.strategy === 'dynamic_pricing') {
      discount = (price.value ?? 0) + '%';
    } else {
      discount = window.foxsell?.formatMoney?.(parseFloat(String(price?.value ?? 0)) * 100);
    }

    const currentStep = optionIndex + 1;

    const currentOption = this.foxsell.config.options[optionIndex];
    const prevOption = optionIndex > 0 ? this.foxsell.config.options[optionIndex - 1] : undefined;

    let isCompleted = (currentStep === maxSteps) && (bundleQuantity >= (currentOption?.quantity ?? 0));

    let currentQuantity = bundleQuantity;
    let requiredQuantity = currentOption?.quantity ?? 0;
    if (prevOption) {
      currentQuantity = bundleQuantity - prevOption.quantity;
      requiredQuantity = (currentOption?.quantity ?? 0) - prevOption.quantity;
    }

    let progress = Math.min(Math.round((currentQuantity / requiredQuantity) * 100), 100);

    return {
      currentStep: bundleQuantity > 0 ? currentStep : 0,
      maxSteps,
      progress,
      isCompleted,
      discount,
      currentQuantity,
      requiredQuantity,
      remainingQuantity: requiredQuantity - currentQuantity
    }
  }


  /**
   * @description Get the bundle progress
   * @returns {FoxSellBundleProgressData | undefined}
   */
  getBundleProgress() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return;
    const qaoEnabled = this.foxsell.bundle.qaoEnabled;
    const progressConfig = qaoEnabled ? this.getBundleProgressWithQAO() : this.getBundleProgressWithoutQAO();
    if (!progressConfig) return;
    const message = this.formatMessage(progressConfig);

    return {
      ...progressConfig,
      qaoEnabled,
      message
    };
  }

  //! DO NOT MODIFY THIS METHOD, extend the class to override this method
  /**
   * @description Update the progress bar
   * @returns {void}
   */
  updateProgressBar() {
    //! you also have access to the following properties:
    //! currentStep, maxSteps, message, progress, qaoEnabled, isCompleted, currentQuantity, requiredQuantity, remainingQuantity
    const progressData = this.getBundleProgress();
    if (!progressData) return;
    const { currentStep, maxSteps, message, progress, qaoEnabled } = progressData;
    const progressLabel = this.querySelector('.foxsell-bundle-progress__label');
    if (progressLabel) {
      progressLabel.innerHTML = message;
    }

    const progressBarsWrapper = this.querySelector('.foxsell-bundle-progress__bars');
    if (!progressBarsWrapper) return;

    progressBarsWrapper.innerHTML = '';

    for (let i = 1; i <= maxSteps; i++) {
      const progressBar = document.createElement('div');
      const progressTrack = document.createElement('div');

      progressTrack.classList.add('foxsell-bundle-progress__track');
      progressBar.classList.add('foxsell-bundle-progress__bar');

      if (i < currentStep) {
        progressBar.style.width = '100%';
      } else if (i == currentStep && qaoEnabled) {
        progressBar.style.width = `${progress}%`;
      } else if (i == currentStep && !qaoEnabled) {
        progressBar.style.width = '100%';
      }

      progressTrack.appendChild(progressBar);
      progressBarsWrapper.appendChild(progressTrack);
    }
  }
}

// @ts-check

class HidroFoxSellBundleProgress extends FoxSellBundleProgress {
  /**
   * @description Update the progress bar
   * @returns {void}
   */
  updateProgressBar() {
    //! you also have access to the following properties:
    //! currentStep, maxSteps, message, progress, qaoEnabled, isCompleted, currentQuantity, requiredQuantity, remainingQuantity
    const progressData = this.getBundleProgress();
    if (!progressData) return;
    const { currentStep, maxSteps, message, progress, qaoEnabled, isCompleted } = progressData;
    const progressLabel = this.querySelector('.foxsell-bundle-progress__label');
    if (progressLabel) {
      progressLabel.innerHTML = message;
    }

    const progressBarsWrapper = this.querySelector('.foxsell-bundle-progress__bars');
    if (!progressBarsWrapper) return;

    /** @type {HTMLElement | null} */
    let progressTrack = progressBarsWrapper.querySelector('.foxsell-bundle-progress__track');
    /** @type {HTMLElement | null} */
    let progressBar = progressBarsWrapper.querySelector('.foxsell-bundle-progress__bar');

    if (!progressTrack || !progressBar) {
      progressBarsWrapper.innerHTML = '';
      progressTrack = document.createElement('div');
      progressBar = document.createElement('div');
      progressTrack.classList.add('foxsell-bundle-progress__track');
      progressBar.classList.add('foxsell-bundle-progress__bar');
      progressTrack.appendChild(progressBar);
      progressBarsWrapper.appendChild(progressTrack);
    }

    let widthPercent = 0;
    if (isCompleted) {
      widthPercent = 100;
    } else if (qaoEnabled && maxSteps > 0) {
      const completedSteps = Math.max(currentStep - 1, 0);
      widthPercent = Math.min(((completedSteps + progress / 100) / maxSteps) * 100, 100);
    } else {
      widthPercent = progress;
    }

    progressBar.style.width = `${widthPercent}%`;
  }
}

// @ts-check
class FoxSellProductForm extends HTMLElement {

  constructor() {
    super();
    this.errorMessages = {
      cartAddFailed: "There was an error adding the item to the cart",
      outOfStock: "Some of the items are out of stock",
    };
  }

  connectedCallback() {
    this.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleSubmit);
  }

  /**
   * @param {FormData} formData
   * @returns {{ key: string; value: FormDataEntryValue }[]}
   */
  getLineAttributes(formData) {
    const attributes = [];
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      const key = match?.[1];
      if (!key || value === '') continue;
      attributes.push({ key, value: value });
    }
    return attributes;
  }

  /** @param {Error} error */
  showError(error) {
    const errorElement = /** @type {HTMLSpanElement | null} */ (this.querySelector('.foxsell-product-form__error'));
    if (errorElement) {
      errorElement.textContent = error.message;
      errorElement.classList.add('active');
    }
  }

  clearError() {
    const errorElement = /** @type {HTMLSpanElement | null} */ (this.querySelector('.foxsell-product-form__error'));
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('active');
    }
  }

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();
    this.clearError();
    
    const form = event.target;
    if(!(form instanceof HTMLFormElement)) return;

    const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    if (submitButton) submitButton.disabled = true;

    const formData = new FormData(/** @type {HTMLFormElement} */ (form));

    if (!window.Shopify.actions.updateCart.isDefault()) {
      try {
        const result = await window.Shopify.actions.updateCart({
          lines: [{
            merchandiseId: `gid://shopify/ProductVariant/${formData.get("id")}`,
            quantity: parseInt(String(formData.get('quantity') ?? '1'), 10),
            attributes: this.getLineAttributes(formData)
          }]
        });

        if(result.warnings?.length || result.userErrors?.length) {
          const warnings = /** @type {Array<{code: string, message: string}>} */ (result.warnings) || [];
          const userErrors = /** @type {Array<{code: string, message: string}>} */ (result.userErrors) || [];

          if(userErrors.length) {
            throw new Error(this.errorMessages.cartAddFailed);
          }

          const warning = warnings.find((warning) => { 
            return (warning.code === "MERCHANDISE_OUT_OF_STOCK" || warning.code === "MERCHANDISE_NOT_ENOUGH_STOCK");
          });

          if (warning) {
            throw new Error(this.errorMessages.outOfStock);
          }
        }
        await window.Shopify.actions.openCart();
      } catch (error) {
        this.showError(/** @type {Error} */ (error));
      }
      finally {
        if (submitButton) submitButton.disabled = false;
      }
    } else {
      this.formSubmit(formData);
    }
  }

  /** @param {FormData} formData */
  async formSubmit(formData) {
    const properties = Object.fromEntries(
      this.getLineAttributes(formData).map(({ key, value }) => [key, value])
    );

    const body = {
      items: [{
        id: formData.get("id"),
        quantity: parseInt(String(formData.get('quantity') ?? '1'), 10),
        properties,
      }]
    };

    try {
      const result = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if(result.ok) {
        window.location.href = window.Shopify.routes.root + 'cart';
      } else {
        const data = await result.json();
        if(data.status === 422) {
          throw new Error(this.errorMessages.outOfStock);
        }
        throw new Error(data.message);
      }
    } catch (error) {
      this.showError(/** @type {Error} */ (error));
    }
  }
}

// JS

const elements = [
  ['foxsell-mix-match', HidroFoxSellMixMatch],
  ['foxsell-category-header', FoxSellCategoryHeader],
  ['foxsell-category-progress', HidroFoxSellCategoryProgress],
  ['foxsell-product-card', HidroFoxSellProductCard],
  ['foxsell-bundle-summary', FoxSellBundleSummary],
  ['foxsell-bundle-line-item', FoxSellBundleLineItem],
  ['foxsell-bundle-progress', HidroFoxSellBundleProgress],
  ['foxsell-variant-radio', FoxSellVariantRadio],
  ['foxsell-variant-select', FoxSellVariantSelect],
  ['foxsell-product-form', FoxSellProductForm],
];


const overrides = window.foxsell?.overrides ?? {};

for (const [name, constructor] of elements) {
  if (customElements.get(name)) continue;
  const factory = overrides[name];
  customElements.define(name, factory ? factory(constructor) : constructor);
}
console.log("[foxsell-hidro] compiled: Wed Aug 19 2026 15:03:24 GMT+0530 (India Standard Time)");
